import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toFixed(2);

export default function PaymentAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  useEffect(() => {
    api.get('/employee/payment-alerts')
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load payment alerts'))
      .finally(() => setLoading(false));
  }, []);

  const sendReminder = async (tenant_id, type) => {
    setSending(prev => ({ ...prev, [tenant_id]: true }));
    try {
      await api.post('/campaigns/send-payment-reminder', { tenant_id, reminder_type: type });
      toast.success('Reminder sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send reminder');
    } finally {
      setSending(prev => ({ ...prev, [tenant_id]: false }));
    }
  };

  const sendBulkReminders = async (tenants, type) => {
    toast(`Sending ${tenants.length} reminders...`);
    for (const t of tenants) {
      try {
        await api.post('/campaigns/send-payment-reminder', { tenant_id: t.id, reminder_type: type });
      } catch {}
    }
    toast.success(`${tenants.length} reminders sent!`);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const { no_payment_method = [], past_due = [] } = data || {};

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Payment Alerts</div>
          <div className="page-sub">Tenants who need payment method reminders or have overdue invoices.</div>

          {/* No payment method */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                ⚠️ No payment method on file
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: 'var(--text-sub)' }}>
                  ({no_payment_method.length} tenant{no_payment_method.length !== 1 ? 's' : ''})
                </span>
              </div>
              {no_payment_method.length > 0 && (
                <button className="btn btn-primary btn-sm" onClick={() => sendBulkReminders(no_payment_method, 'no_payment_method')}>
                  Send all reminders
                </button>
              )}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Tenant</th><th>Property</th><th>Unit</th><th>Phone</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {no_payment_method.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--green)' }}>✅ All active tenants have a payment method on file</td></tr>
                  )}
                  {no_payment_method.map(t => (
                    <tr key={t.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{t.first_name} {t.last_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{t.email}</div>
                      </td>
                      <td style={{ fontSize: 12 }}>{t.property_name}</td>
                      <td style={{ fontWeight: 700 }}>{t.unit_number || '—'}</td>
                      <td style={{ fontSize: 12 }}>{t.phone || '—'}</td>
                      <td>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => sendReminder(t.id, 'no_payment_method')}
                          disabled={sending[t.id]}>
                          {sending[t.id] ? 'Sending...' : '📧 Send reminder'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Past due invoices */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                🔴 Past due invoices
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: 'var(--text-sub)' }}>
                  ({past_due.length} invoice{past_due.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Tenant</th><th>Property</th><th>Unit</th><th>Amount</th><th>Due date</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {past_due.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--green)' }}>✅ No past due invoices</td></tr>
                  )}
                  {past_due.map((inv, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{inv.first_name} {inv.last_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{inv.email}</div>
                      </td>
                      <td style={{ fontSize: 12 }}>{inv.property_name}</td>
                      <td style={{ fontWeight: 700 }}>{inv.unit_number || '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--red)' }}>${fmt(inv.amount)}</td>
                      <td style={{ fontSize: 12, color: 'var(--red)' }}>
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => sendReminder(inv.id, 'payment_failed')}
                          disabled={sending[inv.id]}>
                          {sending[inv.id] ? 'Sending...' : '📧 Send reminder'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
